import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiCode, FiZap, FiUsers, FiHeart } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';

const AboutSection = styled.section<{ $isDarkMode: boolean }>`
  padding: 6rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  background: ${props => props.$isDarkMode 
    ? 'rgba(26,26,46,0.92)' 
    : 'rgba(255,255,255,0.92)'
  };
  border-radius: 24px;
  border: ${props => props.$isDarkMode 
    ? '1px solid rgba(255, 255, 255, 0.1)' 
    : '1px solid rgba(139, 92, 246, 0.1)'
  };

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: clamp(1.75rem, 6vw, 2.5rem);
  }
`;

const SectionSubtitle = styled(motion.p)<{ $isDarkMode: boolean }>`
  font-size: 1.2rem;
  color: ${props => props.$isDarkMode ? '#94a3b8' : '#64748b'};
  text-align: center;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 3rem;
    padding: 0 1rem;
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutContent = styled(motion.div)<{ $isDarkMode: boolean }>`
  color: ${props => props.$isDarkMode ? '#e2e8f0' : '#374151'};
`;

const AboutTitle = styled.h3<{ $isDarkMode: boolean }>`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#1e293b'};

  /* Force visibility in light mode */
  [data-theme="light"] & {
    color: #1e293b !important;
  }
`;

const AboutText = styled(motion.div)<{ $isDarkMode: boolean }>`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  line-height: 1.8;
  color: ${props => props.$isDarkMode ? '#e2e8f0' : '#374151'};
  font-size: 1.25rem;
  font-family: 'Segoe UI', Arial, sans-serif;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    padding: 0 1rem;
  }
`;

const HighlightText = styled.span`
  color: #8b5cf6;
  font-weight: 600;
`;

const SkillsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 2rem;
  }
`;

const SkillCard = styled(motion.div)<{ $isDarkMode: boolean }>`
  background: ${props => props.$isDarkMode 
    ? 'rgba(139, 92, 246, 0.1)' 
    : 'rgba(139, 92, 246, 0.05)'
  };
  border: ${props => props.$isDarkMode 
    ? '1px solid rgba(139, 92, 246, 0.2)' 
    : '1px solid rgba(139, 92, 246, 0.15)'
  };
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: ${props => props.$isDarkMode 
      ? 'rgba(139, 92, 246, 0.4)' 
      : 'rgba(139, 92, 246, 0.3)'
    };
    box-shadow: ${props => props.$isDarkMode 
      ? '0 10px 30px rgba(139, 92, 246, 0.2)' 
      : '0 10px 30px rgba(139, 92, 246, 0.1)'
    };
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const SkillIcon = styled.div`
  font-size: 2.5rem;
  color: #8b5cf6;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SkillTitle = styled.h3<{ $isDarkMode: boolean }>`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#1e293b'};
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const SkillDescription = styled.p<{ $isDarkMode: boolean }>`
  color: ${props => props.$isDarkMode ? '#94a3b8' : '#64748b'};
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const CollaborationSection = styled(motion.div)<{ $isDarkMode: boolean }>`
  background: ${props => props.$isDarkMode 
    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.1))'
    : 'linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(236, 72, 153, 0.05))'
  };
  border: ${props => props.$isDarkMode 
    ? '1px solid rgba(139, 92, 246, 0.2)' 
    : '1px solid rgba(139, 92, 246, 0.15)'
  };
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  margin-top: 4rem;
`;

const CollaborationTitle = styled.h3<{ $isDarkMode: boolean }>`
  font-size: 2rem;
  font-weight: 600;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#1e293b'};
  margin-bottom: 1rem;

  /* Force visibility in light mode */
  [data-theme="light"] & {
    color: #1e293b !important;
  }
`;

const CollaborationText = styled.p<{ $isDarkMode: boolean }>`
  font-size: 1.1rem;
  color: ${props => props.$isDarkMode ? '#94a3b8' : '#64748b'};
  line-height: 1.6;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(139, 92, 246, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(139, 92, 246, 0.4);
  }
`;

const About: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const skills = [
    {
      icon: <FiCode />,
      title: "Full-Stack Development",
      description: "React, Node.js, TypeScript, and modern web technologies"
    },
    {
      icon: <FiZap />,
      title: "Automation & AI",
      description: "Chatbots, automated assistants, and intelligent solutions"
    },
    {
      icon: <FiUsers />,
      title: "Collaboration",
      description: "Team player with experience in group projects and pair programming"
    },
    {
      icon: <FiHeart />,
      title: "Innovation",
      description: "Passionate about creating cutting-edge solutions and user experiences"
    }
  ];



  return (
    <AboutSection id="about" ref={ref} $isDarkMode={isDarkMode}>
      <SectionTitle
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={itemVariants}
      >
        About Me
      </SectionTitle>
      
      <SectionSubtitle
        $isDarkMode={isDarkMode}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={itemVariants}
      >
        Passionate about technology and creating meaningful solutions
      </SectionSubtitle>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
          <img src="/logo192.png" alt="Briddy Profile" style={{ width: 160, height: 160, objectFit: 'cover', borderRadius: 16, boxShadow: '0 4px 24px rgba(139,92,246,0.2)', border: '4px solid #8b5cf6' }} />
        </div>
        <AboutGrid>
          <AboutContent $isDarkMode={isDarkMode} variants={itemVariants}>
            <AboutTitle $isDarkMode={isDarkMode} className="force-visible-text">Who I Am</AboutTitle>
            <AboutText $isDarkMode={isDarkMode} style={{ lineHeight: 1.7, marginBottom: '1.5rem' }}>
              I'm a dedicated full-stack developer passionate about building innovative solutions that make a real impact. AI innovation has lead to many new skills including AI prompt engineering. My journey in tech is driven by curiosity and a desire to create things that matter.<br /><br />
              I specialise in <HighlightText>chatbots</HighlightText>, <HighlightText>automated assistants</HighlightText>, and <HighlightText>modern web applications</HighlightText>—using low code, high code, or full-stack approaches as needed. My portfolio includes projects like <HighlightText>TrackerR</HighlightText> and automation tools that streamline workflows and elevate user experiences.<br /><br />
              With a background in healthcare and finance, I bring a unique perspective to technology, bridging domain expertise with thoughtful digital solutions. I understand the importance of user trust, privacy, and operational efficiency, which shapes how I build secure and practical systems.<br /><br />
              What drives me most is <HighlightText>collaboration</HighlightText>. I thrive on working with talented professionals, sharing insights, and building brilliant things together. Whether it’s pair programming, teamwork, or mentoring, I believe the best results come from collective creativity.
            </AboutText>
          </AboutContent>

          <motion.div variants={itemVariants}>
            <SkillsGrid>
              {skills.map((skill, index) => (
                <SkillCard
                  key={index}
                  $isDarkMode={isDarkMode}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <SkillIcon>{skill.icon}</SkillIcon>
                  <SkillTitle $isDarkMode={isDarkMode}>{skill.title}</SkillTitle>
                  <SkillDescription $isDarkMode={isDarkMode}>{skill.description}</SkillDescription>
                </SkillCard>
              ))}
            </SkillsGrid>
          </motion.div>
        </AboutGrid>

        <CollaborationSection $isDarkMode={isDarkMode} variants={itemVariants}>
          <CollaborationTitle $isDarkMode={isDarkMode} className="force-visible-text">Let's Work Together</CollaborationTitle>
          <CollaborationText $isDarkMode={isDarkMode}>
            I'm always excited to collaborate with other developers, designers, and tech enthusiasts. 
            Whether you have a project in mind, want to share knowledge, or just want to connect, 
            I'd love to hear from you!
          </CollaborationText>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Get In Touch
          </CTAButton>
        </CollaborationSection>
      </motion.div>
    </AboutSection>
  );
};

export default About; 